import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';

export type Theme = 'dark' | 'light';

/**
 * Owns the active light/dark theme. The initial value is set before paint by
 * the inline script in app/index.html (which writes data-theme on <html>); this
 * service reads that back, then keeps it in sync and persists user choices to a
 * cookie (survives SSR/first paint) and localStorage.
 */
export default class ThemeService extends Service {
  @tracked theme: Theme = this.readInitial();

  private readInitial(): Theme {
    if (typeof document === 'undefined') return 'dark';
    const attr = document.documentElement.getAttribute('data-theme');
    if (attr === 'dark' || attr === 'light') return attr;
    return 'dark';
  }

  get isDark(): boolean {
    return this.theme === 'dark';
  }

  toggle = (): void => {
    this.set(this.theme === 'dark' ? 'light' : 'dark');
  };

  set(theme: Theme): void {
    this.theme = theme;
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
      // 1 year, site-wide; available to SSR/first paint.
      document.cookie = `theme=${theme};path=/;max-age=31536000;samesite=lax`;
      try {
        localStorage.setItem('theme', theme);
      } catch {
        /* storage unavailable — cookie still holds the choice */
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    theme: ThemeService;
  }
}
